import { createAsyncThunk } from '@reduxjs/toolkit';
import { getDeliveryAddress, getPVZListV2 } from '@/api/deliveryApi';
import { type Coordinates } from '@/types/ICoordinates';
import { type PVZPointV2Optional } from '@/types/IFetchPvzList';
import { type IGetUserCity } from '@/types/IGetUserCity';
import { getErrorData, type IErrorData } from '@/utils/getErrorData';
import {
  type IFetchClientCoordinatesArgs,
  type IFetchPvzListArgs
} from '@/types/IPvzMapThunkArgs';
import { DEFAULT_PVZ_AVAILABLE_OPERATIONS } from '@/utils/constants/defaultPvzAvailableOperations';

interface IFetchPvzListResult {
  instanceId: string;
  pvzList: PVZPointV2Optional[];
}

interface IFetchClientCoordinatesResult {
  instanceId: string;
  coordinates: Coordinates;
}

interface IRejectPayload {
  instanceId: string;
  error: IErrorData;
}

const buildReject = (instanceId: string, error: unknown): IRejectPayload => {
  return {
    instanceId,
    error: getErrorData(error)
  };
};

/**
 * Загружает список ПВЗ выбранного провайдера для указанного города и
 * сохраняет его в инстансе карты по instanceId. Поддерживает отмену
 * через AbortSignal — отменённый запрос помечается как aborted и не
 * переводит инстанс в состояние ошибки.
 */
export const fetchPvzListThunk = createAsyncThunk<
  IFetchPvzListResult,
  IFetchPvzListArgs,
  { rejectValue: IRejectPayload }
>(
  'pvzMap/fetchPvzList',
  async (
    {
      instanceId,
      providerKey,
      city,
      availableOperations = DEFAULT_PVZ_AVAILABLE_OPERATIONS,
      signal
    },
    { rejectWithValue, signal: thunkSignal }
  ) => {
    try {
      const pvzList = await getPVZListV2(
        providerKey,
        city,
        availableOperations,
        signal ?? thunkSignal
      );
      return { instanceId, pvzList };
    } catch (error) {
      return rejectWithValue(buildReject(instanceId, error));
    }
  }
);

const extractFirstCoordinates = (items: IGetUserCity[]): Coordinates | null => {
  for (const item of items) {
    const lon = item.data.geo_lon;
    const lat = item.data.geo_lat;
    if (
      typeof lon === 'number' &&
      typeof lat === 'number' &&
      !isNaN(lon) &&
      !isNaN(lat)
    ) {
      return [lon, lat];
    }
  }
  return null;
};

/**
 * Геокодирует адрес клиента (строка + город) и сохраняет координаты в
 * инстансе карты. Используется для отображения маркера-ориентира клиента.
 * Поддерживает отмену через AbortSignal — отменённый запрос помечается
 * как aborted и не переводит инстанс в состояние ошибки.
 */
export const fetchClientCoordinatesThunk = createAsyncThunk<
  IFetchClientCoordinatesResult,
  IFetchClientCoordinatesArgs,
  { rejectValue: IRejectPayload }
>(
  'pvzMap/fetchClientCoordinates',
  async (
    { instanceId, address, city, signal },
    { rejectWithValue, signal: thunkSignal }
  ) => {
    try {
      const data = await getDeliveryAddress(
        { query: address, city },
        signal ?? thunkSignal
      );

      const items = data.response?.items;
      if (items === undefined || items === null || items.length === 0) {
        return rejectWithValue(
          buildReject(
            instanceId,
            new Error(
              'Адрес клиента не определён. Возможно, вы выбрали другой город'
            )
          )
        );
      }

      const coordinates = extractFirstCoordinates(items);
      if (coordinates === null) {
        return rejectWithValue(
          buildReject(
            instanceId,
            new Error('Не удалось определить координаты адреса')
          )
        );
      }

      return { instanceId, coordinates };
    } catch (error) {
      return rejectWithValue(buildReject(instanceId, error));
    }
  }
);
